import React, { useContext, useState, useEffect, useRef } from 'react';
import { UserContext } from '../../context/UserContext';
import { notificationsApi } from '../../api';
import { NotificationIcon } from '../../icons';
import './NotificationBell.css';

const NotificationBell = () => {
    const { currentUser } = useContext(UserContext);
    const [unreadCount, setUnreadCount] = useState(0);
    const [notifications, setNotifications] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const dropdownRef = useRef(null);
    const lastFetchRef = useRef(0);
    const FETCH_COOLDOWN = 10000; // 10 секунд cooldown между запросами

    // Загрузка количества непрочитанных уведомлений
    const loadUnreadCount = async () => {
        if (!currentUser) {
            setUnreadCount(0);
            return;
        }

        // Throttle - не чаще чем раз в FETCH_COOLDOWN
        const now = Date.now();
        if (now - lastFetchRef.current < FETCH_COOLDOWN) {
            return;
        }
        lastFetchRef.current = now;

        try {
            const count = await notificationsApi.getUnreadCount();
            setUnreadCount(count);
        } catch (error) {
            console.error('[NotificationBell] Failed to load unread count:', error.message);
        }
    };

    // Загрузка последних уведомлений
    const loadNotifications = async () => {
        if (!currentUser) return;
        setLoading(true);
        try {
            const data = await notificationsApi.list({ page: 0, size: 10 });
            setNotifications(data.content || []);
        } catch (error) {
            console.error('[NotificationBell] Failed to load notifications:', error.message);
        } finally {
            setLoading(false);
        }
    };

    // Отметить уведомление как прочитанное
    const handleMarkAsRead = async (notificationId) => {
        try {
            await notificationsApi.markAsRead(notificationId);
            setUnreadCount(prev => Math.max(0, prev - 1));
            setNotifications(prev =>
                prev.map(n =>
                    n.id === notificationId ? { ...n, isRead: true } : n
                )
            );
        } catch (error) {
            console.error('[NotificationBell] Failed to mark as read:', error.message);
        }
    };

    // Отметить все как прочитанные
    const handleMarkAllAsRead = async () => {
        try {
            await notificationsApi.markAllAsRead();
            setUnreadCount(0);
            setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
        } catch (error) {
            console.error('[NotificationBell] Failed to mark all as read:', error.message);
        }
    };

    // Закрытие dropdown
    const handleClose = () => {
        setIsOpen(false);
    };

    // Клик вне dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                handleClose();
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    // Загрузка данных при открытии
    useEffect(() => {
        if (isOpen && currentUser) {
            loadNotifications();
        }
    }, [isOpen, currentUser]);

    // Периодическое обновление непрочитанных (каждые 60 сек)
    useEffect(() => {
        if (!currentUser) return;

        // Первая загрузка с задержкой чтобы не перегружать при старте
        const timeoutId = setTimeout(() => {
            loadUnreadCount();
        }, 5000);

        const intervalId = setInterval(loadUnreadCount, 60000); // 60 секунд вместо 30

        return () => {
            clearTimeout(timeoutId);
            clearInterval(intervalId);
        };
    }, [currentUser]);

    // Форматирование времени
    const formatTime = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diff = now - date;
        const minutes = Math.floor(diff / 60000);
        const hours = Math.floor(diff / 3600000);
        const days = Math.floor(diff / 86400000);

        if (minutes < 1) return 'Только что';
        if (minutes < 60) return `${minutes} мин назад`;
        if (hours < 24) return `${hours} ч назад`;
        if (days < 7) return `${days} дн назад`;
        return date.toLocaleDateString('ru-RU');
    };

    // Иконки для типов уведомлений
    const getNotificationIcon = (type) => {
        const icons = {
            NEW_COMMENT: '💬',
            NEW_MESSAGE: '📩',
            BATTLE_STARTED: '⚔️',
            ROUND_STARTED: '🎯',
            APPLICATION_APPROVED: '✅',
            APPLICATION_REJECTED: '❌',
            NEW_FOLLOWER: '👤',
            VOTING_STARTED: '🗳️',
            VOTING_ENDED: '🏆',
        };
        return icons[type] || '🔔';
    };

    // Получение ссылки для уведомления
    const getNotificationLink = (notification) => {
        const payload = notification.payload || {};
        switch (notification.type) {
            case 'NEW_COMMENT':
            case 'NEW_MESSAGE':
                return payload.postId ? `/forum/posts/${payload.postId}` : '/forum';
            case 'BATTLE_STARTED':
            case 'ROUND_STARTED':
                return payload.battleId ? `/battles/${payload.battleId}` : '/battles';
            case 'APPLICATION_APPROVED':
            case 'APPLICATION_REJECTED':
                return payload.battleId ? `/battles/${payload.battleId}/participants` : '/battles';
            case 'NEW_FOLLOWER':
                return payload.userId ? `/profile/${payload.userId}` : '/profiles';
            default:
                return '/notifications';
        }
    };

    if (!currentUser) {
        return null;
    }

    return (
        <div className="notification-bell" ref={dropdownRef}>
            <button
                className="notification-bell__button"
                onClick={() => setIsOpen(!isOpen)}
                aria-label="Уведомления"
            >
                <NotificationIcon className="notification-bell__icon" style={{ width: 20, height: 20 }} />
                {unreadCount > 0 && (
                    <span className="notification-bell__badge">
                        {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                )}
            </button>

            {isOpen && (
                <div className="notification-bell__dropdown">
                    <div className="notification-bell__header">
                        <h3 className="notification-bell__title">Уведомления</h3>
                        {unreadCount > 0 && (
                            <button
                                className="notification-bell__mark-all"
                                onClick={handleMarkAllAsRead}
                            >
                                Отметить все как прочитанные
                            </button>
                        )}
                    </div>

                    <div className="notification-bell__list">
                        {loading ? (
                            <div className="notification-bell__loading">Загрузка...</div>
                        ) : notifications.length === 0 ? (
                            <div className="notification-bell__empty">
                                Нет новых уведомлений
                            </div>
                        ) : (
                            notifications.map((notification) => (
                                <a
                                    key={notification.id}
                                    href={getNotificationLink(notification)}
                                    className={`notification-bell__item ${
                                        !notification.isRead ? 'notification-bell__item--unread' : ''
                                    }`}
                                    onClick={() => handleMarkAsRead(notification.id)}
                                >
                                    <span className="notification-bell__item-icon">
                                        {getNotificationIcon(notification.type)}
                                    </span>
                                    <div className="notification-bell__item-content">
                                        <p className="notification-bell__item-title">
                                            {notification.title}
                                        </p>
                                        <p className="notification-bell__item-body">
                                            {notification.body}
                                        </p>
                                        <span className="notification-bell__item-time">
                                            {formatTime(notification.createdAt)}
                                        </span>
                                    </div>
                                </a>
                            ))
                        )}
                    </div>

                    <div className="notification-bell__footer">
                        <a href="/notifications" className="notification-bell__all-link">
                            Все уведомления
                        </a>
                    </div>
                </div>
            )}
        </div>
    );
};

export default NotificationBell;
